<template>
  <div>
    <div class="feedback">
      <transition name="fade" @enter="enterErrorFeedback">
        <feedback-error v-if="showableErrorFeedback" :message="errorMessage" />
      </transition>
    </div>
    <div class="selected-scene-panel__container">
      <SelectedScenePanel
        v-if="isSelectedScene"
        :scene-name="selectedScene.name"
        :scene-description="selectedScene.description"
        :image-url="selectedScene.imageUrl"
      />
    </div>
    <div
      v-resize="setStickyStyleIfNeeded"
      class="search-condition"
      :style="stickyStyle"
    >
      <div class="search-condition--left">
        <FilterModule
          class="search-condition__filter"
          :is-filtered="isFilterActive"
          @clicked="showSearchForm"
        />
        <Sort
          :options="sortOptions"
          :selected-value="sortSelectedValue"
          @change="onSelectSort"
        />
      </div>
    </div>
    <div class="search-result">
      <div v-if="isLoadingItems" class="loading-section">
        <Loading />
      </div>
      <div v-else class="search-result__loaded">
        <div v-if="isNotEmptyResult">
          <div class="search-result__items">
            <ItemBoxesConnected :items="items" />
          </div>
          <div class="search-result__pagination">
            <Pagination
              class="pagination"
              :current-page="currentPage"
              :total-pages="totalPages"
              @clicked="onClickPageLink"
            />
          </div>
        </div>
        <div v-else-if="isLoadedItems" class="empty-result">
          <div class="body">
            条件に一致するギフトは見つかりませんでした
          </div>
          <div
            class="empty-result__retry-message body-lh"
            @click="showSearchForm"
          >
            絞り込み条件を変更する
          </div>
        </div>
      </div>
    </div>
    <SearchForm
      :showable-search-form="showableSearchForm"
      :categories="categories"
      :higher-budget="higherBudget"
      :lower-budget="lowerBudget"
      :scenes="scenes"
      :receiving-kinds="receivingKinds"
      :selected-category-id="selectedCategoryId"
      :selected-scene-id="selectedSceneId"
      :selected-receiving-kind-id="selectedReceivingKindId"
      @hide="hideSearchForm"
      @submit="onSubmitSearchForm"
    />
  </div>
</template>

<script>
import axios from 'axios'
import qs from 'qs'
import url from 'url'
// NOTE: ネストしたクエリパラメータをパースしてくれる
// ref: https://github.com/ljharb/qs
// ref: https://tech-1natsu.hatenablog.com/entry/2018/07/04/175214
import Pagination from '../../organism/paginations/Pagination'
import SearchForm from '../../organism/search_forms/SearchForm'
import FilterModule from '../../atoms/filters/FilterModule'
import ItemBoxesConnected from '../../molecules/item_boxes/ItemBoxesConnected'
import Loading from '../../atoms/loadings/Loading'
import FeedbackError from '../../atoms/feedbacks/feedback-error'
import SelectedScenePanel from '../../organism/items/SelectedScenePanel'
import Sort from '../../atoms/sorts/Sort'

export default {
  components: {
    FilterModule,
    SearchForm,
    Pagination,
    ItemBoxesConnected,
    Loading,
    FeedbackError,
    SelectedScenePanel,
    Sort
  },
  data() {
    return {
      items: [],
      categories: [],
      scenes: [],
      receivingKinds: [],
      sortSelectedValue: 'recommended',
      sortOptions: [
        { value: 'recommended', key: 'おすすめ順' },
        { value: 'price_asc', key: '価格が安い順' },
        { value: 'price_desc', key: '価格が高い順' }
      ],
      isFilterActive: false,
      showableSearchForm: false,
      isLoadingItems: false,
      isLoadedItems: false,
      isAllCategoryChecked: false,
      isAllSceneChecked: false,
      lowerBudget: null,
      higherBudget: null,
      selectedCategoryId: null,
      selectedSceneId: null,
      selectedReceivingKindId: null,
      currentPage: 1,
      totalPages: 1,
      totalItemCountOnSearchForm: null,
      showableErrorFeedback: false,
      errorMessage: '',
      stickyStyle: {}
    }
  },
  computed: {
    isNotEmptyResult() {
      return this.items.length !== 0
    },
    hittedItemCount() {
      return this.totalItemCountOnSearchForm !== null
        ? this.totalItemCountOnSearchForm
        : this.items.length
    },
    isLowerBudgetActive() {
      return this.lowerBudget !== null
    },
    isHigherBudgetActive() {
      return this.higherBudget !== null
    },
    isFiltered() {
      return (
        this.isLowerBudgetActive ||
        this.isHigherBudgetActive ||
        this.selectedCategoryId !== null ||
        this.selectedSceneId !== null ||
        this.selectedReceivingKindId !== null
      )
    },
    selectedScene() {
      return this.selectedSceneId
        ? this.scenes.find(scene => scene.id === this.selectedSceneId)
        : null
    },
    isSelectedScene() {
      return this.selectedSceneId !== null && this.selectedScene !== null
    },
    isNotSelectedScene() {
      return !this.isSelectedScene
    }
  },
  async mounted() {
    this.setStickyStyleIfNeeded()
    await this.getItemCategories()
    await this.getScenes()
    await this.getItemReceivingKinds()
    this.initializeParams()
    await this.search(this.currentPage)

    // NOTE: ブラウザバック・フォワード時の更新処理
    window.onpopstate = async function() {
      this.initializeParams()
      await this.search(this.currentPage)
    }.bind(this)
  },
  methods: {
    initializeParams() {
      const urlQuery = url.parse(location.href).query
      if (!urlQuery) {
        this.sortSelectedValue = 'recommended'
        this.lowerBudget = null
        this.higherBudget = null
        this.selectedCategoryId = null
        this.selectedSceneId = null
        this.selectedReceivingKindId = null
        this.currentPage = 1
        return
      }
      const parsed = qs.parse(urlQuery)
      this.sortSelectedValue = parsed.sort_key ? parsed.sort_key : 'recommended'
      this.lowerBudget = parsed.lower_budget
        ? Number(parsed.lower_budget)
        : null
      this.higherBudget = parsed.higher_budget
        ? Number(parsed.higher_budget)
        : null
      this.selectedCategoryId = parsed.category_id
        ? Number(parsed.category_id)
        : null
      this.selectedSceneId = parsed.scene_id ? Number(parsed.scene_id) : null
      // NOTE: 現状item_receiving_kindsは1つしか設定しないため[0]を取得
      this.selectedReceivingKindId =
        parsed.item_receiving_kinds && parsed.item_receiving_kinds[0]
          ? Number(parsed.item_receiving_kinds[0])
          : null
      this.currentPage = parsed.page_number ? Number(parsed.page_number) : 1
    },
    async onSelectSort(value) {
      this.sortSelectedValue = value
      this.scrollToTop()
      await this.search(1)
      this.updateUrlFromParams()
    },
    showSearchForm() {
      this.showableSearchForm = true
    },
    hideSearchForm() {
      this.showableSearchForm = false
    },
    async onClickPageLink(page) {
      this.scrollToTop()
      await this.search(page)
      this.updateUrlFromParams()
    },
    async onSubmitSearchForm(
      inputtedLowerBudget,
      inputtedHigherBudget,
      inputtedSelectedCategoryId,
      inputtedSelectedSceneId,
      inputtedSelectedReceivingKindId
    ) {
      this.hideSearchForm()
      this.lowerBudget = inputtedLowerBudget
      this.higherBudget = inputtedHigherBudget
      this.selectedCategoryId = inputtedSelectedCategoryId
      this.selectedSceneId = inputtedSelectedSceneId
      this.selectedReceivingKindId = inputtedSelectedReceivingKindId
      await this.search(1)
      this.updateUrlFromParams()
    },
    createParamsObjectFromData(page) {
      const targetPageNumber = page ? page : this.currentPage
      const sortKey =
        this.sortSelectedValue !== 'recommended'
          ? { sort_key: this.sortSelectedValue }
          : {}
      const lowerBudget = this.lowerBudget
        ? { lower_budget: this.lowerBudget }
        : {}
      const higherBudget = this.higherBudget
        ? { higher_budget: this.higherBudget }
        : {}
      const categoryId = this.selectedCategoryId
        ? { category_id: this.selectedCategoryId }
        : {}
      const sceneId = this.selectedSceneId
        ? { scene_id: this.selectedSceneId }
        : {}
      const itemReceivingKinds = this.selectedReceivingKindId
        ? { item_receiving_kinds: [this.selectedReceivingKindId] }
        : {}
      const pageNumber = { page_number: targetPageNumber }
      return Object.assign(
        {},
        sortKey,
        lowerBudget,
        higherBudget,
        categoryId,
        sceneId,
        itemReceivingKinds,
        pageNumber
      )
    },
    updateUrlFromParams() {
      const params = this.createParamsObjectFromData()
      if (params.page_number === 1) {
        delete params.page_number
      }
      const paramsString = qs.stringify(params, { arrayFormat: 'brackets' })
      history.pushState(
        paramsString,
        '',
        !paramsString ? '/items' : `/items?${paramsString}`
      )
    },
    async search(pageNumber) {
      this.items = []
      this.isFilterActive = this.isFiltered
      this.isLoadingItems = true
      this.isLoadedItems = false
      try {
        const subParams = this.createParamsObjectFromData(pageNumber)
        const params = { q: subParams }
        const res = await axios.get('/api/v1/pickup/items', {
          params,
          paramsSerializer: params => {
            return qs.stringify(params, { arrayFormat: 'brackets' })
          }
        })
        if (res.data.data.length === 0) {
          this.isLoadingItems = false
          this.isLoadedItems = true
          return
        }
        const { current_page, total_pages } = res.data.meta
        this.currentPage = current_page
        this.totalPages = total_pages
        this.items = res.data.data
          .map(item => item.attributes)
          .map(item => {
            return {
              id: item.id,
              linkUrl: `/items/${item.id}`,
              imageUrl: item.item_box_connected_image_url,
              brandName: item.brand_name,
              itemName: item.name,
              itemPrice: `¥${item.min_price.toLocaleString()}`,
              isIncludingTax: item.is_including_tax,
              hasMultipleItemSkus: item.item_sku_count > 1,
              isDeliveryItem: item.is_delivery_item
            }
          })
      } catch (error) {
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
      this.isLoadingItems = false
      this.isLoadedItems = true
    },
    async getItemCategories() {
      try {
        const res = await axios.get('/api/v1/pickup/item_categories')
        this.categories = res.data.data.map(item_category => {
          return {
            id: item_category.attributes.id,
            name: item_category.attributes.name
          }
        })
      } catch (error) {
        this.isLoadingItems = false
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    async getScenes() {
      try {
        const res = await axios.get('/api/v1/pickup/scenes')
        this.scenes = res.data.data.map(scene => {
          return {
            id: scene.attributes.id,
            name: scene.attributes.name,
            description: scene.attributes.description,
            imageUrl: scene.attributes.image_url
          }
        })
      } catch (error) {
        this.isLoadingItems = false
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    async getItemReceivingKinds() {
      try {
        const res = await axios.get('/api/v1/pickup/item_receiving_kinds')
        this.receivingKinds = res.data.data.map(receivingKind => {
          return {
            id: receivingKind.attributes.id,
            name: receivingKind.attributes.displayName
          }
        })
      } catch (error) {
        this.isLoadingItems = false
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    scrollToTop() {
      window.scrollTo({ top, behavior: 'smooth' })
    },
    enterErrorFeedback() {
      setTimeout(() => {
        this.showableErrorFeedback = false
      }, 3000)
    },
    setStickyStyleIfNeeded() {
      const headers = document.querySelectorAll('header.header')
      if (headers.length !== 1) {
        return {}
      }
      const headerBottomRect = headers[0].getBoundingClientRect().bottom
      this.stickyStyle = {
        position: 'sticky',
        top: `${headerBottomRect}px`
      }
    }
  }
}
</script>
